"use client"

import { useCallback, useEffect, useId, useMemo, useRef, useState, type ComponentProps, type CSSProperties, type Ref } from "react"

import Form from "next/form"
import { useTranslations } from "next-intl"

import { SearchProductOption } from "@/components/marketplace/layout/search-product-option"
import { ClockIcon, CloseIcon, IconButton, SearchIcon, SearchInput, Text } from "@/components/ui"
import { useRecentSearches } from "@/lib/hooks/search/use-recent-searches"
import { useSearchData } from "@/lib/hooks/search/use-search-data"
import { useClickOutside } from "@/lib/hooks/use-click-outside"
import { useCloseWhenScrolledAway } from "@/lib/hooks/use-close-when-scrolled-away"
import { useContainedScroll } from "@/lib/hooks/use-contained-scroll"
import { useLocalizedPush, useSearchAction } from "@/lib/hooks/use-navigation"
import { buildSearchOptions, exactProductHref, type SearchOption } from "@/lib/services/search/search-options"
import { DEFAULT_SEARCH_QUERY, SEARCH_PARAM_KEYS } from "@/lib/types/search"
import { createConversionId } from "@/platform/observability/analytics/conversions"
import { trackSearch } from "@/platform/observability/analytics/v2/search"

const ROW_LIMIT = 8
const MIN_QUERY = 2
const DEBOUNCE_MS = 250

interface RecentOption {
  kind: "recent"
  key: string
  label: string
  term: string
}

interface HeaderSearchProps {
  value: string
  inputRef?: Ref<HTMLInputElement>
  onValueChange: (value: string) => void
  onSubmitTerm: (term: string) => void
  className?: string
  panelClassName?: string
  panelStyle?: CSSProperties
  placeholder?: string
  tone?: ComponentProps<typeof SearchInput>["tone"]
  size?: ComponentProps<typeof SearchInput>["size"]
  submit?: boolean
  submitVariant?: ComponentProps<typeof SearchInput>["submitVariant"]
  glyph?: boolean
  autoFocusOnMount?: boolean
  onDismiss?: () => void
  onOpenChange?: (open: boolean) => void
}

export const HeaderSearch = ({
  value,
  inputRef,
  onValueChange,
  onSubmitTerm,
  className,
  panelClassName,
  panelStyle,
  placeholder,
  tone = "newgray",
  size = "lg",
  submit = false,
  submitVariant,
  glyph = true,
  autoFocusOnMount = false,
  onDismiss,
  onOpenChange,
}: HeaderSearchProps) => {
  const t = useTranslations("marketplace.searchOverlay")
  const localizedPush = useLocalizedPush()
  const searchAction = useSearchAction()
  const listId = useId()
  const rootRef = useRef<HTMLFormElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(autoFocusOnMount)
  const [active, setActive] = useState(-1)
  const { recent, add: addRecent, remove: removeRecent } = useRecentSearches()

  const [debounced, setDebounced] = useState("")
  useEffect(() => {
    if (value.trim() === debounced) return
    const id = setTimeout(() => setDebounced(value.trim()), DEBOUNCE_MS)
    return () => clearTimeout(id)
  }, [value, debounced])

  const term = value.trim()
  const compactPanel = size === "nav"
  const searching = debounced.length >= MIN_QUERY
  const { data, isFetching, isPlaceholderData } = useSearchData({ ...DEFAULT_SEARCH_QUERY, text: debounced }, 1, { enabled: open && searching })
  const searchOptions = useMemo(
    () => buildSearchOptions({ query: debounced, suggestions: data?.suggestions ?? [], products: data?.products ?? [], limit: ROW_LIMIT }),
    [data, debounced]
  )

  const showRecent = !searching && recent.length > 0
  const options: (SearchOption | RecentOption)[] = useMemo(() => {
    if (showRecent) return recent.map((entry) => ({ kind: "recent", key: `recent:${entry}`, label: entry, term: entry }))
    if (!searching) return []
    return searchOptions
  }, [showRecent, recent, searching, searchOptions])

  useEffect(() => setActive(-1), [debounced, showRecent])
  useClickOutside(rootRef, () => setOpen(false))
  const close = useCallback(() => setOpen(false), [])
  useCloseWhenScrolledAway(rootRef, open, close)

  const expanded = open && (options.length > 0 || searching)
  useContainedScroll(panelRef, expanded)
  useEffect(() => {
    onOpenChange?.(expanded)
  }, [expanded, onOpenChange])
  const openProduct = (href: string) => {
    if (term.length > 0) {
      trackSearch({ search_term: term, results_count: data?.totalCount, conversionId: createConversionId() })
      addRecent(term)
    }
    localizedPush(href)
  }
  const submitSearch = () => {
    setOpen(false)
    const exactHref = debounced === term && !isPlaceholderData ? exactProductHref(data?.products ?? [], term) : undefined
    if (exactHref) return openProduct(exactHref)
    onSubmitTerm(term)
  }
  const go = (option: SearchOption | RecentOption) => {
    setOpen(false)
    if (option.kind !== "recent" && (isPlaceholderData || debounced !== term)) return onSubmitTerm(term)
    if (option.kind === "product") return openProduct(option.href)
    onSubmitTerm(option.term)
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      setOpen(false)
      return onDismiss?.()
    }
    if (event.key === "Enter") {
      const chosen = options[active]
      if (chosen) {
        event.preventDefault()
        return go(chosen)
      }
      event.preventDefault()
      return submitSearch()
    }
    if (!expanded || (event.key !== "ArrowDown" && event.key !== "ArrowUp")) return
    event.preventDefault()
    const last = options.length - 1
    const down = event.key === "ArrowDown"
    setActive((current) => (down ? (current >= last ? -1 : current + 1) : current <= -1 ? last : current - 1))
  }

  return (
    <Form
      ref={rootRef}
      action={searchAction}
      onSubmit={(event) => {
        event.preventDefault()
        submitSearch()
      }}
      className={`relative ${className ?? ""}`}
    >
      <SearchInput
        name={SEARCH_PARAM_KEYS.Q}
        ref={inputRef}
        tone={tone}
        size={size}
        submit={submit}
        submitVariant={submitVariant}
        glyph={glyph}
        autoFocus={autoFocusOnMount}
        placeholder={placeholder ?? t("placeholder")}
        className="w-full"
        role="combobox"
        aria-expanded={expanded}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={active >= 0 && options[active] ? `${listId}-${active}` : undefined}
        value={value}
        onChange={(event) => {
          onValueChange(event.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {expanded && (
        <div
          ref={panelRef}
          style={panelStyle}
          className={`bg-surface shadow-soft rounded-12x py-300 ${panelClassName ?? "z-dropdown absolute top-full right-0 mt-350 w-96 overflow-hidden"}`}
        >
          <ul id={listId} role="listbox" aria-label={t("popularSearches")}>
            {options.map((option, index) => (
              /* eslint-disable-next-line jsx-a11y/click-events-have-key-events */
              <li
                key={option.key}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={index === active}
                onMouseEnter={() => setActive(index)}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => go(option)}
                className={`flex cursor-pointer items-center ${option.kind === "product" ? `${compactPanel ? "px-400" : "px-500"} py-200` : `gap-400 ${compactPanel ? "h-8.25 px-400" : "h-8.5 px-500"}`} ${index === active ? "bg-surface-subtle" : ""}`}
              >
                {option.kind === "product" ? (
                  <SearchProductOption product={option.product} />
                ) : (
                  <>
                    <span className="text-fg-subtle shrink-0">{option.kind === "recent" ? <ClockIcon size={15} /> : <SearchIcon size={15} />}</span>
                    <Text as="span" variant="body" family="text" tone="default" truncate className="min-w-0 flex-1">
                      {option.label}
                    </Text>
                  </>
                )}
                {option.kind === "recent" && (
                  <IconButton
                    aria-label={t("removeRecent", { query: option.term })}
                    variant="ghost"
                    className="text-fg-subtle hover:text-fg size-6 shrink-0"
                    icon={<CloseIcon size={12} />}
                    onClick={(event) => {
                      event.stopPropagation()
                      removeRecent(option.term)
                    }}
                  />
                )}
              </li>
            ))}
          </ul>
          {searching && searchOptions.length === 0 && (
            <Text as="p" variant="bodySm" family="text" tone="muted" className={compactPanel ? "px-400 py-200" : "px-500 py-200"}>
              {isFetching ? t("searching") : t("noSuggestions", { query: debounced })}
            </Text>
          )}
        </div>
      )}
    </Form>
  )
}
